import React from 'react';
import useDynamicTranslation, { UseDynamicTranslationOptions } from '../Hooks/useDynamicTranslation';
import styles from './DynamicTranslatedContent.module.css';

interface DynamicTranslatedContentProps {
    /**
     * The content to translate.
     */
    content: string;

    /**
     * The children to render.
     * If a function is provided, it will be called with the
     * translated content, the loading state, the error, the
     * hasTranslation state, the retry function, and the original content.
     */
    children:
        | React.ReactNode
        | ((props: {
              translatedContent: string;
              isLoading: boolean;
              error: Error | null;
              hasTranslation: boolean;
              retry: () => void;
              originalContent: string;
          }) => React.ReactElement);

    /**
     * Whether to show the loading state.
     */
    showLoadingState?: boolean;

    /**
     * The component to render when the content is loading.
     */
    loadingComponent?: React.ReactElement | null;

    /**
     * The component to render when the content fails to translate.
     */
    errorComponent?: React.ReactElement | null;

    /**
     * The extended options for the useDynamicTranslation hook.
     */
    extendedOptions?: UseDynamicTranslationOptions;
}

/**
 * Component to translate content dynamically.
 * It uses the useDynamicTranslation hook to translate the content and
 * provides a loading state, an error state, and a children function pattern
 * to render the content.
 *
 * @important
 * Please, make sure you combine this component with the `RenderIfVisible` component
 * to avoid hammering the API with requests for content that is not visible to the user.
 * This is especially important for large lists of content, like:
 * ```
 * items.map(item => <DynamicTranslatedContent content={item.content} />)
 * ```
 *
 * @example
 * <RenderIfVisible defaultHeight={200}>
 *     <DynamicTranslatedContent content={item.content} />
 * </RenderIfVisible>
 *
 * @example
 * <CoreDynamicTranslator content="Hello, world!">
 *     {(props) => <div>{props.translatedContent}</div>}
 * </CoreDynamicTranslator>
 *
 */
function DynamicTranslatedContent({
    content,
    children,
    showLoadingState = false,
    loadingComponent = null,
    errorComponent = null,
    extendedOptions = {},
}: DynamicTranslatedContentProps): React.ReactElement {
    const { translatedContent, isLoading, error, hasTranslation, retry } = useDynamicTranslation(content, {
        showLoadingState,
        ...extendedOptions,
    });

    // Handle loading state
    if (isLoading && showLoadingState) {
        if (loadingComponent) {
            return loadingComponent;
        }

        return (
            <div className={styles.DynamicTranslationLoading}>
                <div className={styles.SkeletonText}>
                    {content?.substring(0, Math.min(content.length, 100))}
                    {content && content.length > 100 && '...'}
                </div>
            </div>
        );
    }

    // Handle error state
    if (error && errorComponent) {
        return React.cloneElement(errorComponent, { error, retry, originalContent: content });
    }

    // Render with children function pattern!
    // @example
    // <DynamicTranslatedContent content="Hello, world!">
    //     {(props) => <div>{props.translatedContent}</div>}
    // </DynamicTranslatedContent>
    if (typeof children === 'function') {
        return (
            children as (props: {
                translatedContent: string;
                isLoading: boolean;
                error: Error | null;
                hasTranslation: boolean;
                retry: () => void;
                originalContent: string;
            }) => React.ReactElement
        )({
            translatedContent,
            isLoading,
            error,
            hasTranslation,
            retry,
            originalContent: content,
        });
    }

    // Default rendering
    return <>{translatedContent}</>;
}

export default DynamicTranslatedContent;
